import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  INPUT,
  INPUT_MONO,
  TABLE,
  TD,
  TH,
} from "@/components/tool-kit/styles";
import type { NashEquilibriumStrings } from "@/components/tools/nash-equilibrium-calculator";
import { t } from "@/lib/i18n/interpolate";

export interface Axis {
  id: number;
  name: string;
}

/** Cell payoffs keyed `${rowId}-${colId}`: [row player, column player] as typed. */
export type Cells = Record<string, [string, string]>;

export const MAX_STRATEGIES = 5;
export const MIN_STRATEGIES = 2;

export function cellKey(row: Axis, col: Axis): string {
  return `${row.id}-${col.id}`;
}

/** The editable payoff matrix: strategy names on the edges, two payoffs in each cell. */
export function PayoffGrid({
  rows,
  cols,
  cells,
  rowName,
  colName,
  strings: s,
  onRename,
  onCell,
  onAdd,
  onRemove,
}: {
  rows: Axis[];
  cols: Axis[];
  cells: Cells;
  rowName: string;
  colName: string;
  strings: NashEquilibriumStrings;
  onRename: (side: "row" | "col", id: number, name: string) => void;
  onCell: (key: string, index: 0 | 1, value: string) => void;
  onAdd: (side: "row" | "col") => void;
  onRemove: (side: "row" | "col", id: number) => void;
}) {
  const label = (axis: Axis, side: "row" | "col", index: number) =>
    axis.name.trim() === ""
      ? t(side === "row" ? s.newRowStrategy : s.newColStrategy, {
          n: index + 1,
        })
      : axis.name;
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <caption className="px-3 pt-3 text-left text-muted-foreground text-sm">
            {t(s.gridCaption, { row: rowName, col: colName })}
          </caption>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {rowName} ↓ / {colName} →
              </th>
              {cols.map((col, j) => (
                <th
                  className={`${TH} min-w-[11rem] normal-case`}
                  key={col.id}
                  scope="col"
                >
                  <div className="flex items-center gap-2">
                    <label className="sr-only" htmlFor={`ne-c-${col.id}`}>
                      {t(s.colStrategyName, { n: j + 1 })}
                    </label>
                    <input
                      className={INPUT}
                      id={`ne-c-${col.id}`}
                      onChange={(e) => onRename("col", col.id, e.target.value)}
                      value={col.name}
                    />
                    <button
                      aria-label={t(s.removeStrategy, {
                        strategy: label(col, "col", j),
                      })}
                      className={BUTTON_SMALL}
                      disabled={cols.length <= MIN_STRATEGIES}
                      onClick={() => onRemove("col", col.id)}
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.id}>
                <th className={`${TD} min-w-[10rem] font-normal`} scope="row">
                  <div className="flex items-center gap-2">
                    <label className="sr-only" htmlFor={`ne-r-${row.id}`}>
                      {t(s.rowStrategyName, { n: i + 1 })}
                    </label>
                    <input
                      className={INPUT}
                      id={`ne-r-${row.id}`}
                      onChange={(e) => onRename("row", row.id, e.target.value)}
                      value={row.name}
                    />
                    <button
                      aria-label={t(s.removeStrategy, {
                        strategy: label(row, "row", i),
                      })}
                      className={BUTTON_SMALL}
                      disabled={rows.length <= MIN_STRATEGIES}
                      onClick={() => onRemove("row", row.id)}
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                </th>
                {cols.map((col, j) => {
                  const key = cellKey(row, col);
                  const pair = cells[key] ?? ["", ""];
                  const where = {
                    rowStrategy: label(row, "row", i),
                    colStrategy: label(col, "col", j),
                  };
                  return (
                    <td className={TD} key={key}>
                      <div className="flex items-center gap-2">
                        <label className="sr-only" htmlFor={`ne-a-${key}`}>
                          {t(s.payoffLabel, { player: rowName, ...where })}
                        </label>
                        <input
                          className={`${INPUT_MONO} w-20`}
                          id={`ne-a-${key}`}
                          inputMode="decimal"
                          onChange={(e) => onCell(key, 0, e.target.value)}
                          value={pair[0]}
                        />
                        <label className="sr-only" htmlFor={`ne-b-${key}`}>
                          {t(s.payoffLabel, { player: colName, ...where })}
                        </label>
                        <input
                          className={`${INPUT_MONO} w-20`}
                          id={`ne-b-${key}`}
                          inputMode="decimal"
                          onChange={(e) => onCell(key, 1, e.target.value)}
                          value={pair[1]}
                        />
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          disabled={rows.length >= MAX_STRATEGIES}
          onClick={() => onAdd("row")}
          type="button"
        >
          {t(s.addStrategy, { player: rowName })}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={cols.length >= MAX_STRATEGIES}
          onClick={() => onAdd("col")}
          type="button"
        >
          {t(s.addStrategy, { player: colName })}
        </button>
      </div>
    </div>
  );
}
